import { useEffect, useState } from "react";
import { supAPI, fmtINR } from "./supApi";
import { Boxes, Truck, Warehouse, FileText, ShoppingCart, AlertTriangle, Layers } from "lucide-react";

export default function SupplyDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { supAPI.dashboard().then(setData).catch(() => {}); }, []);

  const c = data?.counts || {};
  const tiles = [
    { label: "Active Items",    value: c.items_active,    icon: Boxes,         accent: "bg-slate-50 text-slate-700"  },
    { label: "Vendors",         value: c.vendors_active,  icon: Truck,         accent: "bg-sky-50 text-sky-700"      },
    { label: "Stores",          value: c.stores_active,   icon: Warehouse,     accent: "bg-emerald-50 text-emerald-700" },
    { label: "PRs Pending",     value: c.prs_pending,     icon: FileText,      accent: "bg-amber-50 text-amber-700"  },
    { label: "Open POs",        value: c.pos_open,        icon: ShoppingCart,  accent: "bg-violet-50 text-violet-700" },
    { label: "Low Stock Items", value: data?.low_stock_count, icon: AlertTriangle, accent: "bg-rose-50 text-rose-700"  },
  ];

  return (
    <div className="space-y-5" data-testid="sup-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className={`w-8 h-8 rounded-lg ${t.accent} flex items-center justify-center mb-2.5`}>
                <Icon className="w-4 h-4" strokeWidth={1.75}/>
              </div>
              <div className="text-[24px] font-display font-semibold text-hocp-navy tabular-nums">{t.value ?? "—"}</div>
              <div className="text-[11.5px] text-slate-500 uppercase tracking-wider">{t.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Low stock items</h2>
            <span className="text-[11px] uppercase text-slate-400 tracking-wider">below reorder level</span>
          </div>
          {!data?.low_stock_top?.length ? (
            <div className="text-slate-400 text-[13px] py-6 text-center">Nothing critical &mdash; you&apos;re stocked.</div>
          ) : (
            <div className="space-y-2">
              {data.low_stock_top.map((r) => (
                <div key={r.id} className="flex items-center justify-between border border-rose-100 bg-rose-50/40 rounded-lg px-3 py-2.5">
                  <div>
                    <div className="text-[13px] font-medium text-hocp-navy">{r.item_name}</div>
                    <div className="text-[11.5px] text-slate-500">{r.item_sku} · {r.store_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[13px] font-semibold text-rose-700 tabular-nums">{r.qty_on_hand} {r.uom}</div>
                    <div className="text-[11px] text-slate-500">reorder ≤ {r.reorder_level}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-hocp-navy on-dark text-white rounded-2xl p-5">
          <h2 className="font-display text-[16px] font-semibold mb-2">Stock Value</h2>
          <div className="text-[36px] font-display font-bold tracking-tight tabular-nums">{fmtINR(data?.total_stock_value)}</div>
          <p className="text-[12px] text-white/60 mt-2">Moving-average valuation across all stores.</p>
          <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2">
            <Layers className="w-4 h-4 text-white/60" strokeWidth={1.75}/>
            <span className="text-[12px] text-white/70">
              {data?.enabled_categories?.length || 0} categor{data?.enabled_categories?.length === 1 ? "y" : "ies"} enabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
